import {
	HomeIcon, PlusIcon, useOpen, Button, GetRowsParams, InfiniteScrollList,
} from '@aleph/shared-components';
import RedditReportsLoader from 'wb-reddit/app/routes/reports/components/loader';
import RedditReportListItem from 'wb-reddit/app/routes/reports/components/report';
import React, { useState, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { motion } from 'framer-motion';
// import RedditNotifications from 'wb-reddit/app/common/components/notifications';
import { RedditHeaderSearch } from 'wb-reddit/app/common/components/headerSearch';
import { ReportRow } from 'wb-reddit/app/routes/reports/utils';
import { useLayoutRef } from 'wb-reddit/app/routes/context/layoutContext';
import useUpdateRows from 'wb-reddit/app/routes/hooks/useUpdateRows';
import { useTranslation } from 'react-i18next';
import { SpotifyReportsDocument, SpotifyReportsQuery } from 'wb-spotify/graphql/generated';
import SpotifyDatasourceSelector from 'wb-spotify/app/routes/reports/create/spotifyDatasourceSelector';
import { uniqBy } from 'lodash';
import useSpotifyRolePermissions from 'wb-spotify/app/common/hooks/useSpotifyPermissions';
import { processSpotifyReport } from 'wb-spotify/app/routes/reports/utils';

const SpotifyReportsList = () => {
	const { t } = useTranslation();

	const [search, setSearch] = useState<string>('');

	const [datasourceSelectorOpen, { onOpen, onClose }] = useOpen(false);

	const [getReports] = useLazyQuery(SpotifyReportsDocument);

	const getRows = useCallback(async (params: GetRowsParams<string | null>) => {
		const { data } = await getReports({
			variables: {
				cursor: params.metaData ?? undefined,
				search: search ? `*${search}*` : '**',
			},
			fetchPolicy: 'network-only',
		});

		const spotifyData = data as SpotifyReportsQuery;
		// TODO remove when when BE solve the issue
		// https://alephrnd.atlassian.net/browse/WB-4755
		const filteredItems = uniqBy(spotifyData?.reports?.items, item => item.reportId);

		return {
			data: filteredItems?.map(
				spotifyReport => (processSpotifyReport(spotifyReport)),
			),
			hasNextPage: !!spotifyData?.reports?.cursor ?? null,
			metaData:    spotifyData?.reports?.cursor ?? null,
		};
	}, [search]);

	const parentElement = useLayoutRef();

	const { exposedMethods, handleUpdateRow } = useUpdateRows<ReportRow>();

	const { canCreateReport } = useSpotifyRolePermissions();

	return (
		<motion.div
			className="flex-1 w-full h-[100vh] flex flex-col"
			initial={{ filter: 'blur(10px)' }}
			animate={{ filter: 'blur(0px)' }}
			exit={{ filter: 'blur(10px)' }}
			transition={{ duration: 0.1 }}
		>
			<div className="flex flex-col gap-[16px] w-full sticky top-0 left-0 bg-gray-0 z-10">
				<div
					className="flex justify-between items-center border-b border-b-black"
					data-testid="reports-header-container"
				>
					<div className="flex gap-[16px] items-center text-gray-700 flex-1 h-[64px]">
						<HomeIcon
							width={24}
							height={24}
						/>

						<div className="text-[18px]">{t('sc: reports', 'Reports')}</div>
					</div>

					<RedditHeaderSearch
						search={search}
						setSearch={setSearch}
					/>

					{/* <div className="flex pl-[24px] h-[64px] items-center"> */}
					{/*	<RedditNotifications /> */}
					{/* </div> */}

				</div>
				{datasourceSelectorOpen && <SpotifyDatasourceSelector onClose={onClose} />}
				{
					canCreateReport && (
						<div className="py-[16px]">
							<Button
								title={t('sc: new', 'New')}
								leftNode={<PlusIcon />}
								onClick={onOpen}
								data-testid="new-report"
							/>
						</div>
					)
				}

				<div
					className="flex h-[36px] items-center font-bold text-gray-950"
				>
					{/* TODO translate */}
					<div className="px-[8px] flex-1">Report</div>
					<div className="px-[8px] w-[80px]">Accounts</div>
					<div className="px-[8px] w-[220px]">Schedule</div>
					<div className="px-[8px] w-[180px]">Metrics</div>
					<div className="px-[8px] w-[260px]" />
				</div>
			</div>

			<div
				id="reports-list"
				className="flex-1 w-full"
			>
				<InfiniteScrollList<string | null, ReportRow>
					// @ts-ignore: TODO: fix this, ok to ignore for now
					getRows={getRows}
					parentElement={parentElement?.current}
					exposedMethods={exposedMethods}
					renderItem={({ props, data, isLoaderRow }) => {
						if (isLoaderRow) {
							return <RedditReportsLoader key={props.key} />;
						}

						return (<RedditReportListItem
							{...props}
							reportItem={data}
							handleUpdateRow={handleUpdateRow}
						/>);
					}}
				/>
			</div>
		</motion.div>
	);
};

export default SpotifyReportsList;
